"use client";

// "Continue with Google" via Google Identity Services (GIS).
//
// GIS only renders its own button inside an iframe we can't restyle, and
// the personalized "Continue as <name>" variant strands the brand "G" with
// an ugly gap. To get a clean, on-brand button while keeping the exact
// ID-token flow the backend already verifies, we render Google's real
// button TRANSPARENTLY on top of our own styled button: the user sees our
// button, their click lands on Google's invisible one underneath, and the
// returned ID token ("credential") flows to onCredential unchanged.
//
// Renders nothing when NEXT_PUBLIC_GOOGLE_CLIENT_ID isn't configured, so
// local/dev builds without Google set up still work — the email flow is
// always available beneath it.

import { useEffect, useRef } from "react";

const CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
const GIS_SRC = "https://accounts.google.com/gsi/client";

interface GoogleIdServices {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (response: { credential?: string }) => void;
        ux_mode?: "popup" | "redirect";
        auto_select?: boolean;
      }) => void;
      renderButton: (
        parent: HTMLElement,
        options: Record<string, unknown>
      ) => void;
    };
  };
}

declare global {
  interface Window {
    google?: GoogleIdServices;
  }
}

/** Google's multi-color "G", per their branding guidelines. */
function GoogleGlyph() {
  return (
    <svg
      className="login-google-glyph"
      width="18"
      height="18"
      viewBox="0 0 48 48"
      aria-hidden="true"
    >
      <path fill="#4285F4" d="M45 24c0-1.6-.14-2.8-.44-4.06H24v7.5h12c-.24 1.9-1.55 4.78-4.46 6.72l-.04.27 6.48 5.02.45.04C42.6 35.9 45 30.5 45 24z" />
      <path fill="#34A853" d="M24 46c5.8 0 10.67-1.9 14.23-5.18l-6.78-5.26c-1.82 1.27-4.26 2.16-7.45 2.16-5.68 0-10.5-3.74-12.22-8.93l-.26.02-6.73 5.2-.09.25C8.2 41.07 15.5 46 24 46z" />
      <path fill="#FBBC05" d="M11.78 28.79A13.86 13.86 0 0 1 11 24c0-1.67.3-3.29.76-4.79l-.01-.32-6.82-5.29-.22.1A22.96 22.96 0 0 0 2 24c0 3.7.9 7.2 2.46 10.3l7.32-5.51z" />
      <path fill="#EB4335" d="M24 9.27c4.03 0 6.75 1.74 8.3 3.2l6.06-5.92C34.65 3.07 29.8 1 24 1 15.5 1 8.2 5.93 4.46 13.7l7.3 5.51C13.5 13.99 18.32 9.27 24 9.27z" />
    </svg>
  );
}

export function GoogleSignInButton({
  onCredential,
}: {
  onCredential: (credential: string) => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Keep the latest callback reachable from GIS's out-of-React callback
  // without re-initializing the button on every render.
  const callbackRef = useRef(onCredential);
  callbackRef.current = onCredential;

  useEffect(() => {
    if (!CLIENT_ID) return;
    let cancelled = false;

    // The invisible GIS button only needs to COVER our visible button so
    // clicks land on it. Match its width to our button (GIS caps at 400).
    function buttonWidth() {
      const w = wrapRef.current?.clientWidth ?? 320;
      return Math.round(Math.min(400, Math.max(240, w)));
    }

    function render() {
      if (cancelled || !window.google?.accounts?.id || !containerRef.current) return;
      window.google.accounts.id.initialize({
        client_id: CLIENT_ID as string,
        callback: (response) => {
          if (response.credential) callbackRef.current(response.credential);
        },
        ux_mode: "popup",
      });
      containerRef.current.innerHTML = "";
      // Appearance is irrelevant (it's transparent); we only care that the
      // click target is tall + wide enough to cover our visible button.
      window.google.accounts.id.renderButton(containerRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "continue_with",
        shape: "pill",
        logo_alignment: "left",
        width: buttonWidth(),
      });
    }

    // Re-fit the overlay when the viewport (and thus the card) resizes.
    let resizeTimer: number | undefined;
    function handleResize() {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => render(), 150);
    }
    window.addEventListener("resize", handleResize);

    if (window.google?.accounts?.id) {
      render();
      return () => {
        cancelled = true;
        window.clearTimeout(resizeTimer);
        window.removeEventListener("resize", handleResize);
      };
    }

    let script = document.querySelector<HTMLScriptElement>(
      `script[src="${GIS_SRC}"]`
    );
    if (!script) {
      script = document.createElement("script");
      script.src = GIS_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener("load", render);
    return () => {
      cancelled = true;
      window.clearTimeout(resizeTimer);
      window.removeEventListener("resize", handleResize);
      script?.removeEventListener("load", render);
    };
  }, []);

  if (!CLIENT_ID) return null;
  return (
    <div ref={wrapRef} className="login-google-wrap">
      {/* The button the user sees — purely visual; the real (transparent)
          Google button below sits on top and captures the click. */}
      <div className="login-google-face" aria-hidden="true">
        <GoogleGlyph />
        <span>Continue with Google</span>
      </div>
      {/* Google's real button, rendered transparent and overlaid. */}
      <div ref={containerRef} className="login-google" />
    </div>
  );
}
